/**
 * DeliveryAddressDialog — the customer's addresses in a modal ("Delivery
 * address"), opened by the picker's Change button. A search box at the top,
 * the matching addresses as rows (the chosen one marked), "Show more" while
 * the book has further pages, and, on the spine book, a way to capture a new
 * address. Clicking a row chooses it and closes the modal.
 *
 * Capture runs inside this modal: the capture form replaces the list and its
 * "Back to addresses" returns to it, so the picker never stacks a second
 * modal. A host that runs its own capture (the delivery-allocation widget's
 * command) closes this modal first for the same reason.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Plus, Search } from "lucide-react";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Input } from "../../primitives/input";
import { CustomerAddressCaptureForm } from "./CustomerAddressCaptureForm";
import {
  capturedToPickerAddress,
  postcodeLine,
  type PickerAddress,
  type PickerAddressPage,
} from "./pickerAddress";

/** How a new address is captured from the modal, absent where the book offers no capture. */
export type AddressCapture =
  /** The built-in form, creating the address under the spine customer. */
  | { kind: "form"; cisCustomerCode: string }
  /** The host's own capture (a command in the command shell); null when cancelled. */
  | { kind: "host"; run: () => Promise<PickerAddress | null> };

/** Loads page `page` (1-based) of the addresses matching the trimmed search. */
export type FetchAddressPage = (
  query: string,
  page: number,
) => Promise<PickerAddressPage>;

const SEARCH_DEBOUNCE_MS = 200;

export function CisOnlyBadge({ className }: { className?: string }) {
  return (
    <Badge
      variant="warning"
      className={className}
      title="Added in One. Tharstern's address book doesn't have this address yet."
    >
      One only
    </Badge>
  );
}

export function DeliveryAddressDialog({
  open,
  onOpenChange,
  chosenId,
  fetchPage,
  capture,
  onChoose,
  returnFocusTo,
  disabled = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The address now chosen, marked in the list. */
  chosenId: string | null;
  fetchPage: FetchAddressPage;
  capture: AddressCapture | null;
  onChoose: (address: PickerAddress) => void;
  /** What opened the modal: focus goes back there when it closes. */
  returnFocusTo: React.RefObject<HTMLElement | null>;
  disabled?: boolean;
}) {
  // The host's capture opens its own dialog as this one closes: focus must
  // not be pulled back to the picker from under it.
  const restoreFocus = useRef(true);

  const captureWithHost = (run: () => Promise<PickerAddress | null>) => {
    restoreFocus.current = false;
    onOpenChange(false);
    void run().then((created) => {
      if (created) onChoose(created);
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[85vh] flex-col sm:max-w-lg"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (restoreFocus.current) returnFocusTo.current?.focus();
          restoreFocus.current = true;
        }}
      >
        <AddressBookContent
          chosenId={chosenId}
          fetchPage={fetchPage}
          capture={capture}
          onChoose={(address) => {
            onChoose(address);
            onOpenChange(false);
          }}
          onCaptureWithHost={captureWithHost}
          disabled={disabled}
        />
      </DialogContent>
    </Dialog>
  );
}

function AddressBookContent({
  chosenId,
  fetchPage,
  capture,
  onChoose,
  onCaptureWithHost,
  disabled,
}: {
  chosenId: string | null;
  fetchPage: FetchAddressPage;
  capture: AddressCapture | null;
  onChoose: (address: PickerAddress) => void;
  onCaptureWithHost: (run: () => Promise<PickerAddress | null>) => void;
  disabled: boolean;
}) {
  const [view, setView] = useState<"list" | "capture">("list");

  if (view === "capture" && capture?.kind === "form") {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Capture a new address</DialogTitle>
          <DialogDescription>
            One will save the address to this customer&rsquo;s addresses
            straight away. This delivery will go to the new address.
          </DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <CustomerAddressCaptureForm
            cisCustomerCode={capture.cisCustomerCode}
            onCreated={(created) => onChoose(capturedToPickerAddress(created))}
            onBack={() => setView("list")}
            disabled={disabled}
          />
        </div>
      </>
    );
  }

  return (
    <AddressList
      chosenId={chosenId}
      fetchPage={fetchPage}
      onChoose={onChoose}
      onCapture={
        capture === null
          ? undefined
          : capture.kind === "form"
            ? () => setView("capture")
            : () => onCaptureWithHost(capture.run)
      }
    />
  );
}

type ListState =
  | { status: "loading"; addresses: PickerAddress[]; page: number }
  | {
      status: "ready";
      addresses: PickerAddress[];
      page: number;
      hasMore: boolean;
    }
  | { status: "error"; addresses: PickerAddress[]; page: number };

function AddressList({
  chosenId,
  fetchPage,
  onChoose,
  onCapture,
}: {
  chosenId: string | null;
  fetchPage: FetchAddressPage;
  onChoose: (address: PickerAddress) => void;
  onCapture: (() => void) | undefined;
}) {
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [list, setList] = useState<ListState>({
    status: "loading",
    addresses: [],
    page: 1,
  });
  // Only the latest request's answer is applied: a slow earlier search must
  // not overwrite a later one.
  const latestRequest = useRef(0);

  const load = useCallback(
    async (search: string, page: number) => {
      const request = ++latestRequest.current;
      setList((prev) => ({
        status: "loading",
        addresses: page === 1 ? [] : prev.addresses,
        page,
      }));
      try {
        const next = await fetchPage(search, page);
        if (request !== latestRequest.current) return;
        setList((prev) => ({
          status: "ready",
          addresses:
            page === 1
              ? next.addresses
              : [...prev.addresses, ...next.addresses],
          page,
          hasMore: next.hasMore,
        }));
      } catch {
        if (request !== latestRequest.current) return;
        setList((prev) => ({
          status: "error",
          addresses: page === 1 ? [] : prev.addresses,
          page,
        }));
      }
    },
    [fetchPage],
  );

  // The first page at once on open, then again (after a pause in typing)
  // whenever the search changes.
  useEffect(() => {
    const search = query.trim();
    const handle = setTimeout(
      () => void load(search, 1),
      search === "" ? 0 : SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(handle);
  }, [query, load]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const isSearching = query.trim() !== "";
  const hasMore = list.status === "ready" && list.hasMore;

  return (
    <>
      <DialogHeader>
        <DialogTitle>Delivery address</DialogTitle>
      </DialogHeader>
      <div className="relative">
        <Search
          aria-hidden
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
        />
        <Input
          ref={searchRef}
          type="search"
          aria-label="Search addresses"
          placeholder="Search by name, address or postcode…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="pl-9"
        />
      </div>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
        {list.addresses.length > 0 && (
          <ul className="space-y-2">
            {list.addresses.map((address) => (
              <AddressRow
                key={address.id}
                address={address}
                isChosen={address.id === chosenId}
                onChoose={() => onChoose(address)}
              />
            ))}
          </ul>
        )}
        {list.status === "loading" && (
          <p
            role="status"
            className="text-muted-foreground flex items-center gap-2 text-sm"
          >
            <Loader2 className="size-4 animate-spin" />
            Loading addresses…
          </p>
        )}
        {list.status === "error" && (
          <div
            role="alert"
            className="flex flex-wrap items-center gap-2 text-sm text-red-700"
          >
            <span>The addresses could not be loaded.</span>
            <Button
              type="button"
              variant="link"
              className="h-auto px-0"
              onClick={() => void load(query.trim(), list.page)}
            >
              Try again
            </Button>
          </div>
        )}
        {list.status === "ready" && list.addresses.length === 0 && (
          <p role="status" className="text-muted-foreground text-sm">
            {isSearching
              ? "No addresses match the search."
              : "This customer has no delivery addresses yet."}
          </p>
        )}
        {hasMore && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => void load(query.trim(), list.page + 1)}
          >
            Show more
          </Button>
        )}
      </div>
      {onCapture && (
        <Button type="button" variant="outline" onClick={onCapture}>
          <Plus className="size-4" />
          Capture a new address
        </Button>
      )}
    </>
  );
}

/** One address: click it to choose it. */
function AddressRow({
  address,
  isChosen,
  onChoose,
}: {
  address: PickerAddress;
  isChosen: boolean;
  onChoose: () => void;
}) {
  return (
    <li
      className={`rounded-md border ${isChosen ? "border-primary bg-primary/5" : ""}`}
    >
      <button
        type="button"
        aria-current={isChosen ? "true" : undefined}
        onClick={onChoose}
        className="focus-visible:ring-ring/50 flex w-full items-start gap-3 rounded-md p-3 text-left outline-none focus-visible:ring-[3px]"
      >
        <span
          aria-hidden
          className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border ${isChosen ? "border-primary" : ""}`}
        >
          {isChosen && <span className="bg-primary size-2 rounded-full" />}
        </span>
        <span className="min-w-0 flex-1 text-sm">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium">{address.name}</span>
            {address.isDefault && <Badge variant="secondary">Default</Badge>}
            {address.isCisOnly && <CisOnlyBadge />}
          </span>
          {address.address && (
            <span className="text-muted-foreground block">
              {address.address}
            </span>
          )}
          <span className="text-muted-foreground block">
            {postcodeLine(address)}
          </span>
        </span>
      </button>
    </li>
  );
}
